import { createRouter, createWebHistory } from 'vue-router';
import { UserManager, WebStorageStateStore } from 'oidc-client';
import { useAuthStore } from '@/store/auth';

const userManager = new UserManager({
  authority: 'https://www.iprod.com.ua/identityservice/',
  client_id: 'avn-web',
  post_logout_redirect_uri: window.location.origin,
  redirect_uri: window.location.origin + '/callback',
  response_type: 'code',
  scope: 'openid profile org offline_access',
  userStore: new WebStorageStateStore({
    store: window.localStorage
  })
});

const routes = [
  // Public routes (non-authenticated)
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'landing',
        component: () => import('@/views/Landing.vue')
      },
      {
        path: 'game',
        name: 'game',
        component: () => import('@/views/Game.vue')
      }
    ]
  },

  // Auth callback
  {
    path: '/callback',
    name: 'callback',
    component: {
      template:
        '<div class="d-flex justify-center align-center" style="height: 100vh;"><v-progress-circular indeterminate color="primary" size="64" /></div>'
    },
    meta: { public: true },
    beforeEnter: async (to, from, next) => {
      try {
        const user = await userManager.signinRedirectCallback();
        const authStore = useAuthStore();
        authStore.setUser(user);
        next('/dashboard');
      } catch (error) {
        console.error('Callback error:', error);
        next('/');
      }
    }
  },

  // Protected routes (authenticated)
  {
    path: '/dashboard',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/Dashboard.vue')
      }
    ]
  },
  {
    path: '/components',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'components',
        component: () => import('@/views/Components.vue')
      },
      {
        path: ':id',
        name: 'component-details',
        component: () => import('@/views/ComponentDetails.vue')
      }
    ]
  },
  {
    path: '/recipes',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'recipes',
        component: () => import('@/views/Recipes.vue')
      },
      {
        path: ':id',
        name: 'recipe-details',
        component: () => import('@/views/RecipeDetails.vue')
      }
    ]
  },
  {
    path: '/orders',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'orders',
        component: () => import('@/views/ProductionOrders.vue')
      },
      {
        path: ':id',
        name: 'order-details',
        component: () => import('@/views/ProductionOrderDetails.vue')
      }
    ]
  },
  {
    path: '/warehouses',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'warehouses',
        component: () => import('@/views/Warehouses.vue')
      },
      {
        path: ':id',
        name: 'warehouse-details',
        component: () => import('@/views/WarehouseDetails.vue')
      }
    ]
  },
  {
    path: '/routes',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'routes',
        component: () => import('@/views/Routes.vue')
      },
      {
        path: ':id',
        name: 'route-details',
        component: () => import('@/views/RouteDetails.vue')
      }
    ]
  },
  {
    path: '/station-types',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'station-types',
        component: () => import('@/views/StationTypes.vue')
      }
    ]
  },
  {
    path: '/stations',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'stations',
        component: () => import('@/views/Stations.vue')
      },
      {
        path: ':id',
        name: 'station-details',
        component: () => import('@/views/StationDetails.vue')
      }
    ]
  },
  {
    path: '/operator-panel',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'operator-panel',
        component: () => import('@/views/OperatorPanel.vue')
      }
    ]
  },
  {
    path: '/wip-console',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'wip-console', component: () => import('@/views/WipConsole.vue') },
      { path: ':id', name: 'wip-progress', component: () => import('@/views/WipProgress.vue') }
    ]
  },
  {
    path: '/lines',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'lines',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Лінії' }
      }
    ]
  },
  {
    path: '/plans',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'plans',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Плани' }
      }
    ]
  },
  {
    path: '/materials',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'materials',
        component: () => import('@/views/Inventory.vue')
      }
    ]
  },
  {
    path: '/products',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'products',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Продукція' }
      }
    ]
  },
  {
    path: '/checks',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'checks',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Перевірки' }
      }
    ]
  },
  {
    path: '/defects',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'defects',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Дефекти' }
      }
    ]
  },
  {
    path: '/reports',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'reports',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Звіти' }
      }
    ]
  },
  {
    path: '/settings',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'settings',
        component: () => import('@/views/Placeholder.vue'),
        meta: { title: 'Налаштування' }
      }
    ]
  },
  {
    path: '/organization',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'organization',
        component: () => import('@/views/Organization.vue')
      }
    ]
  },

  // Catch-all redirect
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { top: 0 };
  }
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check user authentication status
  if (authStore.isLoading) {
    try {
      const user = await userManager.getUser();
      const resolvedUser = user && !user.expired ? user : await userManager.signinSilent();
      if (resolvedUser) authStore.setUser(resolvedUser);
    } catch (error) {
      authStore.clearUser();
    }
  }

  const isAuthenticated = authStore.isAuthenticated;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isPublic = to.matched.some((record) => record.meta.public);

  // If route requires auth and user is not authenticated
  if (requiresAuth && !isAuthenticated) {
    // Redirect to login
    userManager.signinRedirect({ state: { returnUrl: to.fullPath } });
    return;
  }

  // If user is authenticated and tries to access landing page
  if (isAuthenticated && to.name === 'landing') {
    next('/dashboard');
    return;
  }

  next();
});

export default router;
export { userManager };
